import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { TOKENS } from '../../constants/reports/reports-filters';
import { PortfolioManagementContext } from '../portfolio-management';
import { RiskFiltersContext } from '../risk-management/risk-filters-provider';
import { WebSocketContext } from '../websocket-context/websocket-provider';

interface IDecayWidgetParamsProvider {
  children: ReactNode;
}

interface IDecayWidgetProvider {
  getRisks: (accounts: any, currency: string, riskSettings: any, isRecalculate?: boolean) => void;
  delta: any;
  theta: any;
  errors: any;
  isLoading: boolean;
  volShockList: any[];
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  reload: () => void;
  selectedSpots: string[];
  setSelectedSpots: React.Dispatch<React.SetStateAction<string[]>>;
}

export const SPOT_LIST = ['0.1', '0', '-0.1'];

export const DecayWidgetContext = createContext<IDecayWidgetProvider>({} as IDecayWidgetProvider);

export const DecayWidgetProvider = ({ children }: IDecayWidgetParamsProvider) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);

  const [isVisible, setIsVisible] = useState(false);

  const {
    riskSettingsSelected,
    freezeSpotMapRef,
    selectedAccountOption,
    addSpotDataToAccount,
    getDataSpotFromAccount,
  } = useContext(PortfolioManagementContext);

  const { filters } = useContext(RiskFiltersContext);

  const volShockList = filters.volShockList.filter((v: any) => v.isVisible);

  const [isLoading, setIsLoading] = useState(false);
  const [delta, setDelta] = useState<any>(null);
  const [theta, setTheta] = useState<any>(null);
  const [errors, setErrors] = useState<any>(null);
  const [selectedSpots, setSelectedSpots] = useState<string[]>(SPOT_LIST);

  const handleResponse = (response: any, account: any) => {
    if (response?.error) {
      setIsLoading(false);
      setErrors(response.error);

      addSpotDataToAccount({
        account: account,
        dataParent: 'DECAY_DATA',
        data: {
          delta: [],
          theta: [],
        },
        error: response.error,
        riskToken: filters.riskToken,
      });

      return;
    }

    if (!response || !response.result) {
      setIsLoading(false);
      return;
    }

    const processRiskData = (data: any[], riskType: string) => {
      return data.reduce((acc: any, item: any) => {
        const risks = item.report.risks.find((risk: any) => risk.risk_type === riskType);
        const riskValues = risks.breakdown.find((i: any) => i['contract'] === 'ALL')?.risk;

        const sanitizedItem = {
          horizon_date: item.horizon_date,
          vol_shock: item.vol_shock,
          value: riskValues.map((rv: number, index: number) => ({
            spot: SPOT_LIST[index],
            value: rv,
          })),
        };

        if (!acc[item.vol_shock]) {
          acc[item.vol_shock] = [];
        }
        acc[item.vol_shock].push(sanitizedItem);

        return acc;
      }, {});
    };

    const deltaData: Record<string, any> = {};
    const thetaData: Record<string, any> = {};

    Object.keys(response.result).forEach((token: string) => {
      const data = response.result[token];
      deltaData[token] = processRiskData(data, 'DELTA');
      thetaData[token] = processRiskData(data, 'THETA');
    });

    setDelta(deltaData);
    setTheta(thetaData);
    setIsLoading(false);
    setErrors(undefined);

    addSpotDataToAccount({
      account: account,
      dataParent: 'DECAY_DATA',
      data: {
        delta: deltaData,
        theta: thetaData,
      },
      error: null,
      riskToken: filters.riskToken,
    });
  };

  const dataExists = () => {
    const savedData = getSavedData();

    if (!savedData) return null;
  };

  const getSavedData = () =>
    getDataSpotFromAccount(selectedAccountOption, 'DECAY_DATA', filters.riskToken);

  const getRisks = (
    accounts: any,
    riskToken: string,
    riskSettings: any,
    isRecalculate?: boolean,
  ) => {
    if (!isVisible) {
      return;
    }

    if (getSavedData() && !isRecalculate) {
      dataExists();
      return;
    }

    setIsLoading(true);
    const KEY = 'DECAY_WIDGET_RISK_DATA';

    function transformTokenToUnderscore(token: string) {
      const lowerToken = token.toLowerCase();
      const suffixes = ['usdt', 'usdc', 'usd'];

      for (const suffix of suffixes) {
        if (lowerToken.endsWith(suffix)) {
          const base = lowerToken.slice(0, -suffix.length);
          return `${base}_${suffix}`;
        }
      }
      return lowerToken;
    }

    const transformedFreezeSpotData = Object.entries(freezeSpotMapRef.current || {})?.reduce(
      (acc: Record<string, number>, [token, value]) => {
        const found = TOKENS.find((t: any) => t.value.includes(token));
        if (found) {
          acc[found.tokenKey] = value;
        } else {
          acc[transformTokenToUnderscore(token)] = value;
        }
        return acc;
      },
      {} as Record<string, number>,
    );

    const volShocks = riskSettings.volShocks;

    const accountsArray = Array.isArray(accounts) ? accounts : [accounts];

    const sources = accountsArray.map((account) => {
      if (account.exchange === 'otc') {
        return {
          account: account.name,
          venue: account.venue,
        };
      } else {
        return { name: account.name };
      }
    });

    const payload = {
      jsonrpc: '2.0',
      id: KEY,
      method: 'run_risk_engine',
      params: {
        config: {
          RISK_TYPES: ['DELTA', 'THETA'],
          SPOT_SHOCK_TYPE: 'MULTIPLICATIVE',
          SPOT_SHOCKS: SPOT_LIST,
          GROUPBY: ['Contract', 'Expiry', 'Instrument'],
          RISK_TOKEN: riskToken,
          UNDERLYING: [],
          HORIZON_DATES: ['0D', '1D', '2D', '3D', '4D', '5D', '6D', '7D', '8D', '9D', '10D'],
          VOL_SHOCKS: volShocks,
        },
        market_overrides: {
          spot_overrides: {
            all: riskSettingsSelected.isSpotOverride ? '00:00:00' : null,
            token_pairs: riskSettingsSelected.isFreezeSpotOverride
              ? { ...transformedFreezeSpotData }
              : {},
          },
          vol_surface_overrides: { all: riskSettingsSelected.isOtcTraderVols ? 'latest' : null },
        },
        sources: sources,
      },
    };

    const message = JSON.stringify(payload);
    sendMessage(message);
    subscribeToChannel(KEY, (response: any) => handleResponse(response, accounts));
  };

  useEffect(() => {
    if (!isVisible) return;

    const savedData = getSavedData();

    if (savedData) {
      dataExists();

      setErrors(savedData.error || undefined);

      setDelta(savedData.data.delta);
      setTheta(savedData.data.theta);
    } else if (
      selectedAccountOption &&
      selectedAccountOption.name &&
      filters.riskToken &&
      riskSettingsSelected
    ) {
      getRisks(selectedAccountOption, filters.riskToken, riskSettingsSelected);
    }
  }, [selectedAccountOption, filters.riskToken, isVisible]);

  const reload = useCallback(() => {
    getRisks(selectedAccountOption, filters.riskToken, riskSettingsSelected, true);
  }, [isVisible, selectedAccountOption, filters.riskToken, riskSettingsSelected]);

  return (
    <DecayWidgetContext.Provider
      value={{
        getRisks,
        delta,
        theta,
        isLoading,
        volShockList,
        setIsVisible,
        errors,
        reload,
        selectedSpots,
        setSelectedSpots,
      }}
    >
      {children}
    </DecayWidgetContext.Provider>
  );
};
